import { useState } from 'react';

export default function FinalCTA() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.code === 'OK') {
        setIsSubmitted(true);
        form.reset();
        setMessageLength(0);
        
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else if (result.code === 'InvalidParameter' && result.meta?.message === 'form data is spam') {
        setErrorMessage('送信内容がスパムと判定されました。お手数ですが、下記のメールアドレスより直接ご連絡ください。');
      } else {
        setErrorMessage('送信に失敗しました。お手数ですが、下記のメールアドレスより直接ご連絡ください。');
      }
    } catch (error) {
      setErrorMessage('送信に失敗しました。お手数ですが、下記のメールアドレスより直接ご連絡ください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageLength(e.target.value.length);
  };

  return (
    <section id="contact" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-block mb-4 sm:mb-6">
            <span className="border-2 border-[#1e3a8a] text-[#1e3a8a] text-xs font-semibold px-4 sm:px-6 py-2 rounded-full uppercase tracking-wider whitespace-nowrap">
              Contact
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-4 sm:mb-6 leading-tight px-4">
            まずは、お気軽にご相談ください。
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed px-4">
            具体的な企画が未定の段階でも、まずはお気軽にご相談ください。
          </p>
        </div>

        <form
          data-readdy-form
          action="https://readdy.ai/api/form/d5f1ijc7bv8ec1ogue3g"
          method="POST"
          onSubmit={handleSubmit}
          className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-10 lg:p-12 border border-gray-100"
        >
          <div className="space-y-6">
            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                会社名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e3a8a] focus:outline-none transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="株式会社〇〇"
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e3a8a] focus:outline-none transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e3a8a] focus:outline-none transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="example@company.co.jp"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e3a8a] focus:outline-none transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="03-1234-5678"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                disabled={isSubmitting}
                onChange={handleMessageChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#1e3a8a] focus:outline-none transition-colors resize-none text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="ご相談内容をお聞かせください(500文字以内)"
              />
              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                {messageLength}/500文字
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? '送信中...' : '相談フォームより申し込む'}
            </button>
          </div>
        </form>

        {isSubmitted && (
          <div className="mt-8 p-6 bg-green-50 border-2 border-green-500 rounded-2xl animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-green-800 mb-1">
                  送信完了しました！
                </h3>
                <p className="text-sm text-green-700">
                  お問い合わせありがとうございます。担当者より2営業日以内にご連絡させていただきます。
                </p>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mt-8 p-6 bg-red-50 border-2 border-red-500 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-1">
                  送信エラー
                </h3>
                <p className="text-sm text-red-700">
                  {errorMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t-2 border-gray-100">
          <p className="text-center text-sm sm:text-base text-gray-700 mb-4">
            フォームが送信できない場合は、下記より直接ご連絡ください。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="mailto:team.kikuta.staff@gmail.com?subject=【問い合わせ】旅行会社向け 集客・広報支援サービス&body=お名前：%0D%0Aメール：%0D%0A電話番号：%0D%0Aお問い合わせ内容：%0D%0A"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1e3a8a] text-[#1e3a8a] px-6 py-3 rounded-full text-sm sm:text-base font-bold hover:bg-[#1e3a8a] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-lg"></i>
              メールで問い合わせる
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
